// "Highlight with colour" — the column panel's editor for a measure's
// conditional colours (its ADV_FORMAT). Plain words in, the canonical
// ADV_FORMAT string out (colourRules.ts translates; visual-engine renders):
//
//   When  Sales  [is less than ▾]  [another field ▾] [Target ▾]   ● Alert
//   Everything else                                                 ● Good
//
// A theme colours the marks a rule picks out IN ORDER — the 1st match takes
// the theme's 1st colour, the 2nd its 2nd — so three states below target are
// three shades of Alert, each still telling apart in the legend and tooltip.
// Which visual types offer it and what they may say is data
// (styleOptions.json › colourRules).

import { useMemo, useState } from 'react'
import { AlertCircle, Plus, Trash2, Wand2 } from 'lucide-react'
import { VISUAL_THEMES, themePalette } from '@twasta/visual-engine'
import {
  OPS, describeRule, newRule, parseColourRules, ruleProblem, serializeColourRules,
  type ColourRule, type RuleOp,
} from './colourRules'
import DATA from './styleOptions.json'
import { useProjectStore } from '@/store/projectStore'

interface RulesData {
  chart: string[]
  grid: string[]
  pivot: string[]
  markWords: Record<string, string>
  ruleThemes: string[]
}
const RULES = (DATA as unknown as { colourRules: RulesData }).colourRules

export type RuleEngine = 'chart' | 'grid' | 'pivot'

/** Which engine colours this visual type's marks, or null when it has none. */
export function colourRuleEngine(visualId: string | undefined): RuleEngine | null {
  const id = String(visualId || '')
  if (RULES.chart.includes(id)) return 'chart'
  if (RULES.grid.includes(id)) return 'grid'
  if (RULES.pivot.includes(id)) return 'pivot'
  return null
}

export interface ColourRulesEditorProps {
  /** The column's ADV_FORMAT. */
  value: string
  onChange: (adv: string) => void
  /** This column's display label ("Sales"). */
  measure: string
  /** The visual type's catalog ID. */
  visualId: string
  /** Other columns of the visual a rule may compare with (NAME + label). */
  fields: { name: string; label: string; numeric: boolean }[]
  readOnly?: boolean
}

const S = {
  card: {
    border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 10px 8px',
    background: 'var(--color-bg)', display: 'flex', flexDirection: 'column', gap: 8,
  } as React.CSSProperties,
  line: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 } as React.CSSProperties,
  word: { fontSize: 13, color: 'var(--color-text-secondary)' } as React.CSSProperties,
  strong: { fontSize: 13, fontWeight: 600, color: 'var(--color-text)' } as React.CSSProperties,
  input: {
    fontSize: 13, padding: '5px 7px', borderRadius: 5, border: '1px solid var(--color-border)',
    background: 'var(--color-bg-secondary)', color: 'var(--color-text)', minWidth: 0,
  } as React.CSSProperties,
  btn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '5px 10px', borderRadius: 6,
    border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', cursor: 'pointer',
  } as React.CSSProperties,
  icon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6,
    border: '1px solid transparent', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer',
  } as React.CSSProperties,
  hint: { fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 } as React.CSSProperties,
  err: { display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 13, color: 'var(--color-error)' } as React.CSSProperties,
}

function themeLabel(id: string): string {
  const t = VISUAL_THEMES.find((x) => x.id === id)
  return t ? (t.id === 'default' ? 'App colour' : t.label) : id
}

/** A theme's colours; Default = the APPLICATION's colour (the project's
 *  theme_color), never the builder's own accent. */
function paletteOf(id: string, appColor: string | null): string[] {
  try {
    return themePalette(id, typeof document !== 'undefined' ? document.documentElement : null, appColor) ?? []
  } catch {
    return []
  }
}

function Strip({ colors, size = 12 }: { colors: string[]; size?: number }) {
  return (
    <span aria-hidden="true" style={{ display: 'inline-flex', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
      {colors.map((c, i) => <span key={i} style={{ width: size, height: size, background: c }} />)}
    </span>
  )
}

/** Theme picker: a swatch strip per theme, plus "One colour". */
function ColourPick({ rule, onChange, readOnly, engine }: {
  rule: ColourRule
  onChange: (patch: Partial<ColourRule>) => void
  readOnly?: boolean
  engine: RuleEngine
}) {
  const appColor = useProjectStore((st) => st.currentProject?.application?.theme_color) || null
  const themes = RULES.ruleThemes.filter((id) => VISUAL_THEMES.some((t) => t.id === id))
  const single = !rule.theme
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div role="radiogroup" aria-label="Colour" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {themes.map((id) => {
          const on = rule.theme === id
          return (
            <button key={id} type="button" role="radio" aria-checked={on} disabled={readOnly}
              title={themeLabel(id)} onClick={() => onChange({ theme: id, color: '' })}
              style={{
                ...S.btn, padding: '4px 7px', gap: 5,
                borderColor: on ? 'var(--color-accent)' : 'var(--color-border)',
                boxShadow: on ? '0 0 0 1px var(--color-accent)' : undefined,
              }}>
              <Strip colors={paletteOf(id, appColor).slice(0, engine === 'chart' ? 3 : 1)} />
              <span>{themeLabel(id)}</span>
            </button>
          )
        })}
        <button type="button" role="radio" aria-checked={single} disabled={readOnly}
          onClick={() => onChange({ theme: '', color: rule.color || paletteOf(rule.theme || 'alert', appColor)[0] || '#d32f2f' })}
          style={{
            ...S.btn, padding: '4px 7px',
            borderColor: single ? 'var(--color-accent)' : 'var(--color-border)',
            boxShadow: single ? '0 0 0 1px var(--color-accent)' : undefined,
          }}>
          One colour
        </button>
      </div>
      {single ? (
        <label style={{ ...S.line, ...S.word }}>
          <input type="color" value={/^#[0-9a-f]{6}$/i.test(rule.color) ? rule.color : '#d32f2f'} disabled={readOnly}
            onChange={(e) => onChange({ color: e.target.value })}
            style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--color-border)', borderRadius: 4 }} />
          Every match in this colour.
        </label>
      ) : engine === 'chart' ? (
        <span style={{ ...S.line, ...S.word }}>
          <Strip colors={paletteOf(rule.theme, appColor).slice(0, 6)} size={14} />
          1st match, 2nd match, … take these in order.
        </span>
      ) : null}
    </div>
  )
}

export default function ColourRulesEditor(p: ColourRulesEditorProps) {
  const engine = colourRuleEngine(p.visualId)
  // The rules as typed — kept locally so a half-filled rule is not lost while
  // the saved ADV_FORMAT carries only the complete ones.
  const [rules, setRules] = useState<ColourRule[]>(() => parseColourRules(p.value))
  const [lastValue, setLastValue] = useState(p.value)
  if (p.value !== lastValue) {
    // The column changed under us (another column selected, undo): re-read
    // unless it is simply what we last wrote.
    setLastValue(p.value)
    if (p.value !== serializeColourRules(rules)) setRules(parseColourRules(p.value))
  }

  const fieldLabel = useMemo(() => {
    const m = new Map(p.fields.map((f) => [f.name.toLowerCase(), f.label]))
    return (name: string) => m.get(name.toLowerCase()) ?? name
  }, [p.fields])
  const numericFields = p.fields.filter((f) => f.numeric)
  const compareFields = numericFields.length ? numericFields : p.fields
  const marks = RULES.markWords[p.visualId] || 'values'

  if (!engine) return null
  const fieldsAllowed = engine !== 'pivot' && compareFields.length > 0
  const otherwiseAllowed = engine !== 'pivot'

  const commit = (next: ColourRule[]) => {
    setRules(next)
    const adv = serializeColourRules(next)
    setLastValue(adv)
    if (adv !== p.value) p.onChange(adv)
  }
  const patch = (i: number, change: Partial<ColourRule>) =>
    commit(rules.map((r, j) => (j === i ? { ...r, ...change } : r)))
  const remove = (i: number) => commit(rules.filter((_, j) => j !== i))

  const conditions = rules.map((r, i) => ({ r, i })).filter(({ r }) => r.kind !== 'otherwise')
  const otherwiseIdx = rules.findIndex((r) => r.kind === 'otherwise')
  const otherwise = otherwiseIdx >= 0 ? rules[otherwiseIdx] : null

  const addRule = () => {
    const base = newRule()
    commit([...conditions.map((c) => c.r), base, ...(otherwise ? [otherwise] : [])])
  }
  const presets: { label: string; make: () => ColourRule[] }[] = []
  if (fieldsAllowed) {
    const target = compareFields.find((f) => /target|budget|plan|goal|quota/i.test(`${f.name} ${f.label}`)) ?? compareFields[0]
    presets.push({
      label: `Below ${target.label}`,
      make: () => [
        newRule({ op: '<', against: 'field', field: target.name, theme: 'alert' }),
        newRule({ kind: 'otherwise', theme: 'good' }),
      ],
    })
  }
  presets.push({ label: 'Below zero', make: () => [newRule({ op: '<', value: '0', theme: 'alert' })] })

  const summary = rules
    .filter((r) => !ruleProblem(r))
    .map((r) => `${describeRule(r, p.measure, fieldLabel)} → ${r.theme ? themeLabel(r.theme) : r.color}`)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={S.hint}>
        Colour the {marks} of <b>{p.measure}</b> when a condition is true. Anything no rule matches keeps the
        visual&apos;s own colour.
      </div>

      {!rules.length && !p.readOnly && (
        <div style={{ ...S.line }}>
          <Wand2 size={14} aria-hidden="true" style={{ color: 'var(--color-text-secondary)' }} />
          <span style={S.word}>Start from</span>
          {presets.map((pr) => (
            <button key={pr.label} type="button" style={S.btn} onClick={() => commit(pr.make())}>{pr.label}</button>
          ))}
        </div>
      )}

      {conditions.map(({ r, i }, n) => {
        const problem = ruleProblem(r)
        return (
          <div key={i} style={S.card}>
            <div style={{ ...S.line, justifyContent: 'space-between' }}>
              <span style={S.strong}>{n === 0 ? 'When' : 'Or when'}</span>
              {!p.readOnly && (
                <button type="button" style={S.icon} aria-label={`Remove rule ${n + 1}`} title="Remove this rule"
                  onClick={() => remove(i)}>
                  <Trash2 size={15} />
                </button>
              )}
            </div>
            {r.kind === 'custom' ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={S.word}>Condition (written by hand — #value# is {p.measure}, #NAME# another field)</span>
                <input type="text" value={r.formula} disabled={p.readOnly} style={{ ...S.input, fontFamily: 'var(--font-mono, monospace)' }}
                  onChange={(e) => patch(i, { formula: e.target.value })} />
              </label>
            ) : (
              <div style={S.line}>
                <span style={S.strong}>{p.measure}</span>
                <select value={r.op} disabled={p.readOnly} style={S.input} aria-label="Comparison"
                  onChange={(e) => {
                    const op = e.target.value as RuleOp
                    patch(i, op === 'between' ? { op, against: 'number' } : { op })
                  }}>
                  {OPS.map((o) => (
                    <option key={o.op} value={o.op}>{o.words}</option>
                  ))}
                </select>
                {fieldsAllowed && r.op !== 'between' && (
                  <select value={r.against} disabled={p.readOnly} style={S.input} aria-label="Compare with"
                    onChange={(e) => {
                      const against = e.target.value as 'number' | 'field'
                      patch(i, against === 'field' && !r.field ? { against, field: compareFields[0]?.name ?? '' } : { against })
                    }}>
                    <option value="number">a number</option>
                    <option value="field">another field</option>
                  </select>
                )}
                {r.against === 'field' && r.op !== 'between' ? (
                  <select value={r.field} disabled={p.readOnly} style={S.input} aria-label="Field"
                    onChange={(e) => patch(i, { field: e.target.value })}>
                    {!compareFields.some((f) => f.name === r.field) && <option value={r.field}>{r.field || 'Choose…'}</option>}
                    {compareFields.map((f) => <option key={f.name} value={f.name}>{f.label}</option>)}
                  </select>
                ) : (
                  <>
                    <input type="number" value={r.value} disabled={p.readOnly} placeholder="0" aria-label="Number"
                      style={{ ...S.input, width: 90 }} onChange={(e) => patch(i, { value: e.target.value })} />
                    {r.op === 'between' && (
                      <>
                        <span style={S.word}>and</span>
                        <input type="number" value={r.value2} disabled={p.readOnly} placeholder="0" aria-label="Second number"
                          style={{ ...S.input, width: 90 }} onChange={(e) => patch(i, { value2: e.target.value })} />
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            <span style={S.strong}>Colour</span>
            <ColourPick rule={r} engine={engine} readOnly={p.readOnly} onChange={(c) => patch(i, c)} />
            {problem && (
              <span style={S.err} role="alert"><AlertCircle size={14} aria-hidden="true" />{problem}</span>
            )}
          </div>
        )
      })}

      {!p.readOnly && (
        <button type="button" style={{ ...S.btn, alignSelf: 'flex-start' }} onClick={addRule}>
          <Plus size={14} aria-hidden="true" /> Add a condition
        </button>
      )}

      {otherwiseAllowed && conditions.length > 0 && (
        <div style={S.card}>
          <label style={{ ...S.line, cursor: p.readOnly ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={!!otherwise} disabled={p.readOnly}
              onChange={(e) => commit(e.target.checked
                ? [...conditions.map((c) => c.r), newRule({ kind: 'otherwise', theme: 'good' })]
                : conditions.map((c) => c.r))} />
            <span style={S.strong}>Everything else</span>
            <span style={S.word}>gets its own colour</span>
          </label>
          {otherwise && (
            <ColourPick rule={otherwise} engine={engine} readOnly={p.readOnly}
              onChange={(c) => patch(otherwiseIdx, c)} />
          )}
        </div>
      )}

      {engine === 'pivot' && (
        <div style={S.hint}>In a pivot a rule compares the cell&apos;s own value with a number; a theme paints its first colour.</div>
      )}
      {summary.length > 0 && (
        <div style={{ ...S.hint, borderLeft: '3px solid var(--color-border)', paddingLeft: 8 }} aria-live="polite">
          {summary.map((s, k) => <div key={k}>{s}</div>)}
        </div>
      )}
    </div>
  )
}
